type PlaylistItemsResponse = {
	etag: string
	items: PlaylistItem[]
}

type VideosResponse = {
	items: VideoItem[],
}

type PlaylistItem = {
	snippet: { title: string, resourceId: { videoId: VideoID } }
}

type VideoItem = {
	id: VideoID
	statistics: { viewCount: string, likeCount: string }
}

type VideoData = {
	artist: string
	song: string
	country: string
	countryCode: string
}

type Video = {
	musicVideo?: VideoID
	nationalFinal?: VideoID
	views?: number
	likes?: number
}

export type Playlists = {
	[year: string]: { musicVideo: string, nationalFinal: string }
}

// 11-character Youtube video ID
type VideoID = string

export default class YoutubeAPI {
	static endpoint = "https://youtube.googleapis.com/youtube/v3"
	static localStorageVersion = 2

	private key: string
	year: number
	playlists: Playlists

	videos: Map<string, Video>

	constructor(key: string, year: number, playlists: Playlists) {
		this.key = key
		this.year = year
		this.playlists = playlists
	}

	async init() {
		this.videos = YoutubeAPI.checkLocalStorage(this.year)

		if (this.videos == undefined || this.videos.size == 0) {
			this.videos = await this.getVideoIDs()
			if (this.videos.size > 0) YoutubeAPI.saveLocalStorage(this.videos, this.year)
		}

		await this.initVideoStats()
	}

	private async getVideoIDs() {
		const videos = new Map()
		
		if (this.playlists[this.year] == undefined) return videos
		
		const musicVideosPromise = this.playlists[this.year].musicVideo
			&& this.getPlaylistVideos(this.playlists[this.year].musicVideo)
		const nationalFinalsPromise = this.playlists[this.year].nationalFinal
			&& this.getPlaylistVideos(this.playlists[this.year].nationalFinal)

		if (musicVideosPromise) {
			const musicVideos = await musicVideosPromise
			for (const musicVideo of musicVideos.items) {
				let { country } = YoutubeAPI.parseVideoTitle(musicVideo.snippet.title) ?? {}
				if (country == undefined) continue
				country = country.toLowerCase()
				if (!videos.has(country)) videos.set(country, {})
				videos.get(country).musicVideo = musicVideo.snippet.resourceId.videoId
			}
		}

		if (nationalFinalsPromise) {
			const nationalFinals = await nationalFinalsPromise
			for (const nationalFinal of nationalFinals.items) {
				let { country } = YoutubeAPI.parseVideoTitle(nationalFinal.snippet.title) ?? {}
				if (country == undefined) continue
				country = country.toLowerCase()
				if (!videos.has(country)) videos.set(country, {})
				videos.get(country).nationalFinal = nationalFinal.snippet.resourceId.videoId
			}
		}

		return videos
	}

	private async initVideoStats() {
		const videoIDs = []
		for (const video of this.videos.values()) {
			if (video.musicVideo) videoIDs.push(video.musicVideo)
			else if (video.nationalFinal) videoIDs.push(video.nationalFinal)
		}

		const videoStats = await this.getVideoStats(videoIDs)
		for (const video of this.videos.values()) {
			let vid = videoStats.items.find(vid => vid.id == video.musicVideo || vid.id == video.nationalFinal)
			if (vid) {
				video.views = Number(vid.statistics.viewCount)
				video.likes = Number(vid.statistics.likeCount)
			}
		}
	}

	// Gets the Youtube video ID for either the music video or the national final
	getVideoID(country: string): VideoID {
		if (!this.videos.has(country.toLowerCase())) return undefined
		const video = this.videos.get(country.toLowerCase())
		return (video.musicVideo != undefined) ? video.musicVideo : video.nationalFinal
	}

	private async getPlaylistVideos(playlistID: string): Promise<PlaylistItemsResponse> {
		return await fetch(YoutubeAPI.endpoint + "/playlistItems?" + new URLSearchParams({
			part: "snippet",
			maxResults: "50",
			playlistId: playlistID,
			fields: "etag,items(snippet(title,resourceId/videoId))",
			key: this.key,
		})).then(x => x.json())
	}

	private async getVideoStats(videoIDs: string[]): Promise<VideosResponse> {
		return await fetch(YoutubeAPI.endpoint + "/videos?" + new URLSearchParams({
			part: "statistics",
			id: videoIDs.join(","),
			fields: "items(id,statistics(viewCount,likeCount))",
			key: this.key,
		})).then(x => x.json())
	}

	static getURLFromVideoID: (videoId: VideoID) => string = videoId => `https://youtu.be/${videoId}`

	private static checkLocalStorage(year: number): Map<string, { musicVideo?: VideoID, nationalFinal?: VideoID }> {
		if (Number(localStorage.getItem("videos_version")?.valueOf()) != YoutubeAPI.localStorageVersion) {
			localStorage.clear()
			localStorage.setItem(`videos_version`, YoutubeAPI.localStorageVersion.toString())
		}
		
		if (!localStorage.getItem(`videos_${year}`)) return
		if (new Date(localStorage.getItem(`videos_${year}_date`)).valueOf() + 1000 * 60 * 60 * 24 < Date.now()) {
			// expired (1 day)
			localStorage.removeItem(`videos_${year}`)
			localStorage.removeItem(`videos_${year}_date`)
			return
		}
		return new Map(JSON.parse(localStorage.getItem(`videos_${year}`)))
	}

	private static saveLocalStorage(videos: Map<string, Video>, year: number) {
		localStorage.setItem(`videos_${year}`, JSON.stringify(Array.from(videos.entries())))
		localStorage.setItem(`videos_${year}_date`, new Date(Date.now()).toISOString())
	}

	private static parseVideoTitle(title: string): VideoData {
		const matches = title.match(/^(.+?)\s+-\s+(.+?)\s+[-|]\s+(.+?)\s+([🇦-🇿]{2})/u)
		if (matches == undefined) return
		return {
			artist: matches[1],
			song: matches[2],
			country: matches[3],
			countryCode: flagEmojiToCountryCode(matches[4]),
		}
	}
}

const flagEmojiStart = "🇦".codePointAt(0)
const asciiStart = "A".codePointAt(0)

const emojiToAscii = (emoji: string): string =>
	String.fromCodePoint(emoji.codePointAt(0) - flagEmojiStart + asciiStart)

const flagEmojiToCountryCode = (flag: string): string =>
	[...flag].map(emojiToAscii).join("").toLowerCase()
